import { Injectable } from '@angular/core';
import { CONTROLLERS, ENDPOINTS } from '../constants';
import { ApiService } from './api.service';

// Task 3510. Tells the Controller API that the logged-in user landed on a tracked page, so the SuperAdmin Dashboard can show
// which practices use it. The user and practice come from the token on the server; the page key must be one the
// server tracks (see TrackedPages in the Controller API).
export const TRACKED_PAGES = {
  KPI_DASHBOARD: 'kpi-dashboard',
  AR_AUTOMATION: 'ar-automation-menu',
  CAMPAIGNS: 'campaigns',
  PAYMENTS: 'payments-menu',
  PAYMENT_PLANS: 'payment-plans-menu',
  PRACTICE_MENU: 'practice-menu',
};

// The menus whose submenu clicks are counted: every submenu item lives under /<menu>/<submenu>/... A rule with a submenu
// counts only that one (Campaigns is a submenu of A/R Automation, so it counts for both).
interface MenuRule {
  menu: string;
  submenu?: string;
  key: string;
}

const MENU_RULES: MenuRule[] = [
  { menu: 'ar-automation', key: TRACKED_PAGES.AR_AUTOMATION },
  { menu: 'ar-automation', submenu: 'campaign', key: TRACKED_PAGES.CAMPAIGNS },
  { menu: 'payments', key: TRACKED_PAGES.PAYMENTS },
  { menu: 'payment-plan', key: TRACKED_PAGES.PAYMENT_PLANS },
  { menu: 'practice', key: TRACKED_PAGES.PRACTICE_MENU },
];

@Injectable({
  providedIn: 'root',
})
export class PageVisitService {
  private api: string = CONTROLLERS.PAGE_VISIT;

  constructor(private apiService: ApiService) {}

  // Every click on a submenu item is one visit: it is recorded when the submenu in the url changes, or when the user comes into
  // the menu from elsewhere. Moving around inside a submenu (its tabs, detail pages) is not counted. Called on every finished
  // navigation.
  private currentSubmenu: { [key: string]: string | null } = {};

  onNavigated(url: string) {
    const path = (url || '').split(/[?#]/)[0];
    for (const rule of MENU_RULES) {
      const match = new RegExp(`^/${rule.menu}/([^/]+)`).exec(path);
      const submenu = match && (!rule.submenu || match[1] === rule.submenu) ? match[1] : null;
      if (submenu && submenu !== this.currentSubmenu[rule.key]) {
        this.track(rule.key);
      }
      this.currentSubmenu[rule.key] = submenu;
    }
  }

  // Fire and forget: tracking must never get in the user's way, so a failure is ignored.
  track(pageKey: string) {
    this.apiService
      .post(`${this.api}/${ENDPOINTS.PAGE_VISIT.TRACK}`, { pageKey })
      .subscribe({ error: () => {} });
  }
}
